import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Globe, MapPin, Users, CalendarDays, Monitor, Smartphone, Tablet } from "lucide-react";

const RANGES = [
  { label: "Today", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "All time", days: 0 },
];

function countryFlag(code) {
  if (!code || code.length !== 2) return "🌐";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function DeviceIcon({ device }) {
  const cls = "w-3.5 h-3.5 inline-block mr-1 -mt-0.5";
  if (device === "Mobile") return <Smartphone className={cls} strokeWidth={1.6} />;
  if (device === "Tablet") return <Tablet className={cls} strokeWidth={1.6} />;
  return <Monitor className={cls} strokeWidth={1.6} />;
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white dark:bg-white/[0.04] border border-paw-border dark:border-white/10 rounded-2xl px-5 py-4">
      <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/50 mb-1.5">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
        <span className="font-manrope text-[12px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-outfit font-bold text-2xl text-paw-text dark:text-white">{value}</p>
    </div>
  );
}

export default function AdminVisitors() {
  const [data, setData] = useState({ visits: [], total: 0, today: 0, countries_count: 0, top_countries: [], top_cities: [], geo_enabled: true });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(0);
  const [country, setCountry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "300" });
      if (days > 0) params.set("days", String(days));
      if (country) params.set("country", country);
      const { data } = await api.get(`/admin/visits?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days, country]);

  const maxCountry = data.top_countries[0]?.count || 1;

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-6xl" data-testid="admin-visitors">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-paw-text dark:text-white flex items-center gap-2.5">
            <Globe className="w-7 h-7 text-paw-accent" strokeWidth={1.6} /> Visitors
          </h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-1">
            Where your website traffic is coming from. City and country only, no IP addresses are stored.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-paw-border dark:border-white/10 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.label}
                onClick={() => setDays(r.days)}
                className={`px-3 py-2 font-manrope text-[13px] transition-colors ${days === r.days ? "bg-paw-accent text-white" : "bg-white dark:bg-white/[0.04] text-paw-text-secondary dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5"}`}
                data-testid={`visitors-range-${r.days}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="p-2.5 rounded-xl border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5" data-testid="visitors-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!data.geo_enabled && (
        <div className="mb-5 rounded-xl bg-paw-warning/10 border border-paw-warning/25 px-4 py-3 font-manrope text-[13px] text-paw-text dark:text-white/80">
          The offline location database isn't installed on the server yet, so visits will show as “Unknown” until it downloads. It fetches automatically on the next backend start.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Users} label="Total visits" value={(data.total || 0).toLocaleString()} />
        <StatCard icon={CalendarDays} label="Today" value={(data.today || 0).toLocaleString()} />
        <StatCard icon={Globe} label="Countries" value={(data.countries_count || 0).toLocaleString()} />
        <StatCard icon={MapPin} label="Cities (top)" value={(data.top_cities?.length || 0).toLocaleString()} />
      </div>

      {country && (
        <button onClick={() => setCountry(null)} className="mb-4 inline-flex items-center gap-1.5 font-manrope text-[13px] text-paw-accent hover:underline" data-testid="visitors-clear-country">
          ← Clear filter: {country}
        </button>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Country breakdown */}
        <div className="lg:col-span-1 bg-white dark:bg-white/[0.04] border border-paw-border dark:border-white/10 rounded-2xl p-5">
          <h2 className="font-outfit font-semibold text-paw-text dark:text-white mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-paw-accent" /> Top countries</h2>
          {loading && !data.top_countries.length ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-paw-text-muted" /></div>
          ) : !data.top_countries.length ? (
            <p className="font-manrope text-sm text-paw-text-muted py-6 text-center">No visits yet.</p>
          ) : (
            <div className="space-y-2.5">
              {data.top_countries.map((c) => (
                <button
                  key={c.country + c.country_code}
                  onClick={() => setCountry(c.country)}
                  className="w-full text-left group"
                  data-testid={`visitors-country-${c.country_code || c.country}`}
                >
                  <div className="flex items-center justify-between font-manrope text-[13px] mb-1">
                    <span className="text-paw-text dark:text-white/80 group-hover:text-paw-accent transition-colors">
                      <span className="mr-1.5">{countryFlag(c.country_code)}</span>{c.country}
                    </span>
                    <span className="text-paw-text-muted dark:text-white/50 tabular-nums">{c.count.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                    <div className="h-full bg-paw-accent text-white/70 rounded-full" style={{ width: `${Math.max(4, (c.count / maxCountry) * 100)}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}

          {data.top_cities?.length > 0 && (
            <>
              <h2 className="font-outfit font-semibold text-paw-text dark:text-white mt-6 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-paw-accent" /> Top cities</h2>
              <div className="space-y-1.5">
                {data.top_cities.map((c) => (
                  <div key={c.city + c.country} className="flex items-center justify-between font-manrope text-[13px]">
                    <span className="text-paw-text dark:text-white/80 truncate">{c.city}<span className="text-paw-text-muted dark:text-white/40">, {c.country}</span></span>
                    <span className="text-paw-text-muted dark:text-white/50 tabular-nums ml-2">{c.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Visit log */}
        <div className="lg:col-span-2 bg-white dark:bg-white/[0.04] border border-paw-border dark:border-white/10 rounded-2xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-paw-border dark:border-white/10 flex items-center justify-between">
            <h2 className="font-outfit font-semibold text-paw-text dark:text-white">Recent visits</h2>
            <span className="font-manrope text-[12px] text-paw-text-muted dark:text-white/40">{country ? `Filtered: ${country}` : "Most recent first"}</span>
          </div>
          {loading && !data.visits.length ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-paw-text-muted" /></div>
          ) : !data.visits.length ? (
            <p className="font-manrope text-sm text-paw-text-muted py-16 text-center">No visits recorded yet. They’ll appear here as people browse the site.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="font-manrope text-[11px] uppercase tracking-wider text-paw-text-muted dark:text-white/40 border-b border-paw-border dark:border-white/10">
                    <th className="px-4 py-2.5 font-medium">Location</th>
                    <th className="px-4 py-2.5 font-medium">Page</th>
                    <th className="px-4 py-2.5 font-medium hidden sm:table-cell">Device</th>
                    <th className="px-4 py-2.5 font-medium text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.visits.map((v, i) => (
                    <tr key={i} className="border-b border-paw-border/50 dark:border-white/5 hover:bg-black/[0.015] dark:hover:bg-white/[0.02]" data-testid={`visit-row-${i}`}>
                      <td className="px-4 py-2.5 font-manrope text-[13px] text-paw-text dark:text-white/85 whitespace-nowrap">
                        <span className="mr-1.5">{countryFlag(v.country_code)}</span>
                        {(v.city && v.city !== "Unknown") ? v.city : "Unknown city"}
                        <span className="text-paw-text-muted dark:text-white/40">, {v.country}</span>
                        {v.region ? <span className="text-paw-text-muted dark:text-white/30 hidden md:inline"> · {v.region}</span> : null}
                      </td>
                      <td className="px-4 py-2.5 font-manrope text-[13px] text-paw-text-secondary dark:text-white/60 max-w-[180px] truncate">{v.path || "/"}</td>
                      <td className="px-4 py-2.5 font-manrope text-[13px] text-paw-text-secondary dark:text-white/60 hidden sm:table-cell whitespace-nowrap"><DeviceIcon device={v.device} />{v.browser || v.device}</td>
                      <td className="px-4 py-2.5 font-manrope text-[12px] text-paw-text-muted dark:text-white/40 text-right whitespace-nowrap">{timeAgo(v.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
